import {
  type ClientMessage,
  type ServerMessage,
  STREAM_PATH,
  ServerMessageSchema,
} from '@pm2-monitor/shared';
import { streamOrigin } from './connection';

export type StreamStatus = 'connecting' | 'open' | 'closed';

interface StreamHandlers {
  onMessage: (message: ServerMessage) => void;
  onStatusChange: (status: StreamStatus, detail?: string) => void;
}

const MAX_BACKOFF_MS = 30_000;
const BASE_BACKOFF_MS = 1_000;

/** Sent by the bridge when the token is rejected; not worth retrying. */
const UNAUTHORIZED = 1008;

/**
 * Live connection to the bridge.
 *
 * A dashboard left open on a second monitor outlives laptop sleeps, VPN
 * changes and bridge restarts, so reconnection is the normal case rather than
 * an error path. Backoff is exponential with jitter so restarting the bridge
 * does not produce a synchronised reconnect from every open tab at once.
 */
export class BridgeStream {
  private socket: WebSocket | null = null;
  private attempt = 0;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private closedByUser = false;
  private pendingSubscription: ClientMessage | null = null;

  constructor(
    private readonly token: string,
    private readonly handlers: StreamHandlers,
  ) {}

  connect(): void {
    this.closedByUser = false;
    this.open();
  }

  private open(): void {
    this.handlers.onStatusChange('connecting');

    // A browser cannot set an Authorization header on the WebSocket
    // handshake, so the token goes in the query string — the bridge accepts
    // either. The connection is wss:// in production, so it is not on the wire
    // in clear text, but it does land in the bridge's request log.
    const url = `${streamOrigin()}${STREAM_PATH}?token=${encodeURIComponent(this.token)}`;

    let socket: WebSocket;
    try {
      socket = new WebSocket(url);
    } catch (error) {
      this.handlers.onStatusChange('closed', String(error));
      this.scheduleReconnect();
      return;
    }
    this.socket = socket;

    socket.onopen = () => {
      this.attempt = 0;
      this.handlers.onStatusChange('open');
      // A new socket starts with no subscription; re-apply the last one.
      if (this.pendingSubscription) this.send(this.pendingSubscription);
    };

    socket.onmessage = (event) => {
      let parsed: unknown;
      try {
        parsed = JSON.parse(String(event.data));
      } catch {
        return;
      }
      // Validate rather than trust: a schema drift between console and bridge
      // should surface as a dropped frame, not a crash mid-render.
      const result = ServerMessageSchema.safeParse(parsed);
      if (result.success) this.handlers.onMessage(result.data);
    };

    socket.onerror = () => {
      // `onclose` always follows, so reconnection is scheduled from there only.
    };

    socket.onclose = (event) => {
      this.socket = null;
      if (event.code === UNAUTHORIZED) {
        // The token is wrong or was rotated. Retrying cannot fix that, and a
        // reconnect loop would hammer the bridge with rejected handshakes.
        this.closedByUser = true;
        this.handlers.onStatusChange('closed', 'unauthorized');
        return;
      }
      this.handlers.onStatusChange('closed');
      this.scheduleReconnect();
    };
  }

  private scheduleReconnect(): void {
    if (this.closedByUser) return;
    const backoff = Math.min(MAX_BACKOFF_MS, BASE_BACKOFF_MS * 2 ** this.attempt);
    const jittered = backoff * (0.5 + Math.random() * 0.5);
    this.attempt += 1;
    this.timer = setTimeout(() => this.open(), jittered);
  }

  send(message: ClientMessage): void {
    if (message.type === 'subscribe_logs' || message.type === 'unsubscribe_logs') {
      this.pendingSubscription = message.type === 'subscribe_logs' ? message : null;
    }
    if (this.socket?.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify(message));
    }
  }

  /** Reconnect now rather than waiting out the backoff — used by "Retry". */
  retryNow(): void {
    if (this.timer) clearTimeout(this.timer);
    this.attempt = 0;
    this.closedByUser = false;
    if (this.socket) this.socket.close();
    else this.open();
  }

  close(): void {
    this.closedByUser = true;
    if (this.timer) clearTimeout(this.timer);
    this.socket?.close();
    this.socket = null;
  }
}
